import React, { useEffect, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Building2, CalendarClock, ChevronDown, Settings2, Timer } from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import useCobroStore from '../../../../store/cobro';
import useTheme from '../../../../hooks/useTheme';
import { Stat } from '../../../../components/ui/Stat';
import { Skeleton } from '../../../../components/ui/Skeleton';
import { Button } from '../../../../components/ui/Button';
import { Badge } from '../../../../components/ui/Badge';
import { InfoHint } from '../../../../components/ui/InfoHint';
import { cn } from '../../../../utils/cn';
import { formatCurrency, formatDayKey } from '../utils/cobroHelpers';
import {
  buildAgingRows,
  formatAsOf,
  formatDays,
  resolveDashboardCurrency,
  splitAmounts,
  toPercent,
} from '../utils/collectionsDashboard';

// Estado de plegado de "Antigüedad y morosos". Es una preferencia de vista por navegador,
// no un dato: si el storage no está disponible (modo privado, cuota) solo no se recuerda.
const AGING_OPEN_KEY = 'beartrack.cobro.dashboard.agingOpen';

const readAgingOpen = () => {
  try {
    return window.localStorage.getItem(AGING_OPEN_KEY) === '1';
  } catch {
    return false;
  }
};

const writeAgingOpen = (open) => {
  try {
    window.localStorage.setItem(AGING_OPEN_KEY, open ? '1' : '0');
  } catch {
    // Sin storage el plegado vuelve al default en la próxima visita; no hay nada que avisar.
  }
};

// Colores del gráfico. recharts pinta atributos SVG, donde var(--token) no resuelve, así que
// necesita colores concretos. Van fijos por tema (valores de design-tokens.scss) en vez de
// leerse con getComputedStyle: ThemeProvider cambia la clase del <html> en un efecto que
// corre DESPUÉS del render de los hijos, y leer los tokens acá devolvería los del tema
// anterior. Solo dos series por estado —vencido y no vencido— con el rótulo del tramo en el
// eje: el color nunca es la única pista.
const CHART_COLORS = {
  light: { overdue: '#B91C1C', current: '#9E9E9E', grid: '#E3E3E3', tick: '#666666' },
  dark: { overdue: '#FCA5A5', current: '#666666', grid: '#292929', tick: '#9E9E9E' },
};

const compactNumber = new Intl.NumberFormat('es-CL', {
  notation: 'compact',
  maximumFractionDigits: 1,
});

const formatOthers = (others) =>
  others.map((o) => formatCurrency(o.amount, o.currency)).join(' · ');

/** Tooltip de un tramo: monto en la moneda graficada, cantidad de notas y otras monedas. */
function AgingTooltip({ active, payload, currency }) {
  const { t } = useTranslation();
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  return (
    <div className="rounded-md border border-border bg-bg p-2 text-xs shadow-lg">
      <div className="font-semibold text-fg">{t(row.label)}</div>
      <div className="text-fg tabular-nums">{formatCurrency(row.amount, currency)}</div>
      <div className="text-fg-muted">{t('{{n}} notas', { n: row.notes })}</div>
      {row.others.length > 0 && (
        <div className="text-fg-muted">{t('además {{montos}}', { montos: formatOthers(row.others) })}</div>
      )}
    </div>
  );
}

function AgingChart({ rows, currency }) {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const colors = CHART_COLORS[isDark ? 'dark' : 'light'];
  const data = useMemo(() => rows.map((r) => ({ ...r, name: t(r.label) })), [rows, t]);

  return (
    <div className="min-w-0">
      <h4 className="text-sm font-semibold text-fg mb-0.5">{t('Antigüedad de la deuda')}</h4>
      <p className="text-xs text-fg-muted mb-2">{t('Montos en {{currency}}', { currency })}</p>
      {/* Barras horizontales: los rótulos de los tramos ("Sin vencimiento") no entran
          debajo de 7 barras verticales en media pantalla. */}
      <div aria-hidden="true">
        <ResponsiveContainer width="100%" height={data.length * 32 + 16}>
          <BarChart data={data} layout="vertical" margin={{ top: 4, right: 12, bottom: 4, left: 4 }}>
            <CartesianGrid horizontal={false} stroke={colors.grid} strokeDasharray="3 3" />
            <XAxis
              type="number"
              tick={{ fontSize: 11, fill: colors.tick }}
              tickFormatter={(v) => compactNumber.format(v)}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              type="category"
              dataKey="name"
              width={104}
              tick={{ fontSize: 11, fill: colors.tick }}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip
              cursor={{ fill: colors.grid, fillOpacity: 0.4 }}
              content={<AgingTooltip currency={currency} />}
            />
            <Bar dataKey="amount" radius={[0, 4, 4, 0]} maxBarSize={18}>
              {data.map((row) => (
                <Cell key={row.bucket} fill={row.overdue ? colors.overdue : colors.current} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-fg-muted">
        <span className="inline-flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-sm" style={{ background: colors.overdue }} aria-hidden />
          {t('Vencidas')}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-sm" style={{ background: colors.current }} aria-hidden />
          {t('No vencidas')}
        </span>
      </div>
      {/* Vista en tabla para lectores de pantalla: el SVG no se puede recorrer. */}
      <table className="sr-only">
        <caption>{t('Antigüedad de la deuda')}</caption>
        <tbody>
          {data.map((row) => (
            <tr key={row.bucket}>
              <th scope="row">{row.name}</th>
              <td>{t('{{n}} notas', { n: row.notes })}</td>
              <td>{formatCurrency(row.amount, currency)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Deudor de una fila de "Mayores morosos": un comercio o, desde la nota consolidada, un
 * holding (billing manda `debtorType`; uno anterior solo `companyId`, que es un comercio).
 */
const debtorOf = (d) => ({
  debtorType: d.debtorType === 'holding' ? 'holding' : 'company',
  companyId: d.companyId ?? null,
  holdingId: d.holdingId ?? null,
  companyName: d.companyName ?? null,
});

function TopDebtors({ items, currency, onSelect, onConfigure }) {
  const { t } = useTranslation();
  return (
    <div className="min-w-0">
      <h4 className="text-sm font-semibold text-fg mb-0.5">{t('Mayores morosos')}</h4>
      <p className="text-xs text-fg-muted mb-2">{t('Montos en {{currency}}', { currency })}</p>
      {items.length === 0 ? (
        <p className="text-sm text-fg-muted">{t('No hay comercios con deuda.')}</p>
      ) : (
        <ol className="rounded-lg border border-border divide-y divide-border">
          {items.map((d, i) => {
            const debtor = debtorOf(d);
            const isHolding = debtor.debtorType === 'holding';
            const name = d.companyName || t('Sin nombre');
            const oldest = formatDayKey(d.oldestDueDateLocal);
            const overdue = Number(d.overdueAmount) > 0;
            return (
              <li
                key={`${debtor.debtorType}:${isHolding ? debtor.holdingId : debtor.companyId}`}
                className="flex items-stretch"
              >
                <button
                  type="button"
                  onClick={() => onSelect?.(debtor)}
                  title={t('Filtrar la lista por {{name}}', { name })}
                  className="flex-1 min-w-0 flex items-center gap-3 px-3 py-2 text-left transition-colors hover:bg-bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500"
                >
                  <span className="w-5 shrink-0 text-xs text-fg-muted tabular-nums">{i + 1}</span>
                  <span className="flex-1 min-w-0">
                    <span className="flex items-center gap-1.5 min-w-0">
                      <span className="truncate text-sm font-medium text-fg">{name}</span>
                      {isHolding && (
                        <Badge tone="info" className="shrink-0">
                          {t('Holding')}
                        </Badge>
                      )}
                    </span>
                    <span className="block truncate text-xs text-fg-muted">
                      {t('{{n}} notas impagas', { n: d.notes })}
                      {oldest &&
                        ` · ${
                          overdue
                            ? t('Venció el {{vencimiento}}', { vencimiento: oldest })
                            : t('Vence el {{vencimiento}}', { vencimiento: oldest })
                        }`}
                    </span>
                  </span>
                  <span className="shrink-0 text-right">
                    <span
                      className={cn(
                        'block text-sm font-semibold tabular-nums',
                        overdue ? 'text-status-error' : 'text-fg',
                      )}
                    >
                      {formatCurrency(d.overdueAmount, d.currency || currency)}
                    </span>
                    <span className="block text-xs text-fg-muted tabular-nums">
                      {t('de {{monto}} impago', {
                        monto: formatCurrency(d.unpaidAmount, d.currency || currency),
                      })}
                    </span>
                  </span>
                </button>
                {/* Botón aparte (no anidado): la fila filtra la lista, este abre la regla. Un
                    holding no tiene regla de comercio: su plazo y su contacto están en su
                    ficha, y los recordatorios para holdings llegan en una fase posterior. */}
                {onConfigure && !isHolding && (
                  <button
                    type="button"
                    onClick={() => onConfigure({ companyId: d.companyId, companyName: d.companyName ?? null })}
                    aria-label={t('Configurar la cobranza de {{name}}', { name })}
                    title={t('Configurar la cobranza de {{name}}', { name })}
                    className="shrink-0 px-3 text-fg-muted transition-colors hover:bg-bg-muted hover:text-fg focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500"
                  >
                    <Settings2 className="w-4 h-4" aria-hidden />
                  </button>
                )}
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}

/**
 * Resumen de cobranza de TODO el operador, arriba de la lista de notas de /admin/cobro:
 * 4 KPIs y una sección plegable con la antigüedad de la deuda y los mayores morosos.
 *
 * Es independiente de la lista: sus números NO siguen los filtros (lo dice el rótulo), y si
 * el endpoint falla la lista sigue funcionando. Un rol sin permiso (403) no ve el bloque.
 *
 * @param {{ ownerId: string|null, operatorCurrency: string,
 *           onSelectDebtor?: (debtor: { debtorType: 'company'|'holding', companyId: string|null,
 *             holdingId: string|null, companyName: string|null }) => void,
 *           onConfigureDebtor?: (debtor: { companyId: string, companyName: string|null }) => void,
 *           activeListFilter?: string,
 *           onListFilter?: (filter: 'overdue'|'dueSoon'|'multipleUnpaid') => void }} props
 *   `onConfigureDebtor` agrega a cada comercio moroso un botón para abrir su regla de
 *   cobranza (a un holding no: no tiene regla de comercio).
 *   `onListFilter` vuelve botones de filtro a "Monto vencido", "Por vencer (7 días)" y
 *   "Comercios con más de 1 nota impaga": cada una reporta su filtro y `activeListFilter`
 *   marca la que está puesta.
 */
export default function CobroDashboard({
  ownerId,
  operatorCurrency = 'CLP',
  onSelectDebtor,
  onConfigureDebtor,
  activeListFilter = '',
  onListFilter,
}) {
  const { t } = useTranslation();
  const panelId = useId();
  const { dashboard, loading, error, fetchDashboard } = useCobroStore((s) => ({
    dashboard: s.dashboardOwnerId === ownerId ? s.dashboard : null,
    loading: s.loadingDashboard,
    error: s.dashboardError,
    fetchDashboard: s.fetchDashboard,
  }));
  const [agingOpen, setAgingOpen] = useState(readAgingOpen);

  useEffect(() => {
    if (ownerId) fetchDashboard(ownerId);
  }, [ownerId, fetchDashboard]);

  const currency = useMemo(
    () => resolveDashboardCurrency(dashboard, operatorCurrency),
    [dashboard, operatorCurrency],
  );
  const agingRows = useMemo(
    () => buildAgingRows(dashboard?.aging, currency),
    [dashboard, currency],
  );

  const toggleAging = () =>
    setAgingOpen((open) => {
      writeAgingOpen(!open);
      return !open;
    });

  if (!ownerId) return null;

  if (!dashboard) {
    if (error?.status === 403) return null;
    if (error && !loading) {
      return (
        <div className="bill-note mb-4 flex flex-wrap items-center justify-between gap-2" role="alert">
          <span>{t('No se pudo cargar el resumen de cobranza.')}</span>
          <Button size="sm" variant="outline" onClick={() => fetchDashboard(ownerId)}>
            {t('Reintentar')}
          </Button>
        </div>
      );
    }
    return (
      <div className="mb-4" role="status" aria-live="polite">
        <span className="sr-only">{t('Cargando…')}</span>
        <Skeleton className="h-3 w-48 mb-2" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-[104px] rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  const overdue = splitAmounts(dashboard.overdue?.amountByCurrency, currency);
  const dueSoon = splitAmounts(dashboard.dueSoon7d?.amountByCurrency, currency);
  const avg = dashboard.avgDaysToPay ?? {};
  const manualPct = toPercent(avg.manualShare);
  const withOthers = (hint, others) =>
    others.length ? `${hint} · ${t('además {{montos}}', { montos: formatOthers(others) })}` : hint;
  // Props de una tarjeta que filtra la lista; sin `onListFilter` queda de solo lectura.
  const listFilterCard = (filter) =>
    onListFilter
      ? { onClick: () => onListFilter(filter), pressed: activeListFilter === filter }
      : {};

  return (
    <section className="mb-4" aria-label={t('Resumen de cobranza')}>
      <p className="text-xs text-fg-muted mb-2">
        {t('Todo el operador · al {{fecha}}', { fecha: formatAsOf(dashboard.asOf) })}
      </p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
        <Stat
          label={t('Monto vencido')}
          value={formatCurrency(overdue.amount, currency)}
          hint={withOthers(
            t('{{n}} notas · {{c}} comercios', {
              n: dashboard.overdue?.notes ?? 0,
              c: dashboard.overdue?.companies ?? 0,
            }),
            overdue.others,
          )}
          icon={<AlertTriangle />}
          tone="error"
          {...listFilterCard('overdue')}
        />
        <Stat
          label={t('Por vencer (7 días)')}
          value={formatCurrency(dueSoon.amount, currency)}
          hint={withOthers(t('{{n}} notas', { n: dashboard.dueSoon7d?.notes ?? 0 }), dueSoon.others)}
          icon={<CalendarClock />}
          tone="warning"
          {...listFilterCard('dueSoon')}
        />
        <Stat
          label={t('Comercios con más de 1 nota impaga')}
          value={dashboard.companiesWithMultipleUnpaid?.count ?? 0}
          hint={t('{{n}} notas impagas en total', { n: dashboard.unpaid?.notes ?? 0 })}
          icon={<Building2 />}
          tone="info"
          {...listFilterCard('multipleUnpaid')}
        />
        <Stat
          label={
            <span className="inline-flex items-center gap-1">
              {t('Días promedio de pago (90 d)')}
              <InfoHint
                srLabel={t('Cómo se calcula')}
                align="end"
                label={
                  <>
                    {t('Días entre la emisión y el pago de {{n}} notas pagadas en los últimos 90 días.', {
                      n: avg.n ?? 0,
                    })}
                    {manualPct > 0 && (
                      <>
                        {' '}
                        {t('{{pct}} % se marcó como pagada a mano: en esas, la fecha de pago es la del registro.', {
                          pct: manualPct,
                        })}
                      </>
                    )}
                  </>
                }
              />
            </span>
          }
          value={formatDays(avg.avg)}
          hint={
            avg.n > 0
              ? t('Mediana: {{m}} d', { m: formatDays(avg.median) })
              : t('Sin notas pagadas en los últimos 90 días')
          }
          icon={<Timer />}
          tone="neutral"
        />
      </div>

      <div className="rounded-xl border border-border bg-bg shadow-soft">
        <button
          type="button"
          onClick={toggleAging}
          aria-expanded={agingOpen}
          aria-controls={agingOpen ? panelId : undefined}
          className="w-full flex items-center justify-between gap-2 px-4 py-3 text-left rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
        >
          <span className="text-sm font-semibold text-fg">{t('Antigüedad y morosos')}</span>
          <ChevronDown
            className={cn('w-4 h-4 text-fg-muted transition-transform', agingOpen && 'rotate-180')}
            aria-hidden
          />
        </button>
        {agingOpen && (
          <div id={panelId} className="grid gap-6 lg:grid-cols-2 border-t border-border p-4">
            <AgingChart rows={agingRows} currency={currency} />
            <TopDebtors
              items={dashboard.topDebtors ?? []}
              currency={currency}
              onSelect={onSelectDebtor}
              onConfigure={onConfigureDebtor}
            />
          </div>
        )}
      </div>
    </section>
  );
}
